import { type ReactElement } from 'react'
import { type PermissionEntityScope, type PermissionType } from '@terros/common'
import { Badge, Table } from '@mantine/core'
import { useTranslation, type usePermissionComparisons } from '@front/model'
import { ENTITY_COLUMN_WIDTH } from './permissionComparisonConstants'
import { getComparisonPermissionScope } from './compareUtil'

type PermissionScopeCellProps = {
  isControl: boolean
  isDifferent: boolean
  permissionType: PermissionType
  result: ReturnType<typeof usePermissionComparisons>[number]
}

const SCOPE_COLOR: Record<PermissionEntityScope, string> = {
  none: 'gray',
  self: 'blue',
  downline: 'teal',
  company: 'green',
  withinRestrictions: 'orange',
}

export const PermissionScopeCell = ({
  isControl,
  isDifferent,
  permissionType,
  result,
}: PermissionScopeCellProps): ReactElement => {
  const { t } = useTranslation()
  const scope = getComparisonPermissionScope(result.data?.list, permissionType)
  const scopeLabel: Record<PermissionEntityScope, string> = {
    none: t('Disabled'),
    self: t('Self'),
    downline: t('Team'),
    company: t('Company'),
    withinRestrictions: t('Visibility restrictions'),
  }
  let className: string | undefined
  if (isControl) className = 'bg-brand-light'
  else if (isDifferent) className = 'bg-yellow-light'

  return (
    <Table.Td className={className} ta='center' w={ENTITY_COLUMN_WIDTH}>
      {result.data ? (
        <Badge color={SCOPE_COLOR[scope]} variant='light'>
          {scopeLabel[scope]}
        </Badge>
      ) : (
        <Badge color={result.error ? 'red' : 'gray'} variant='light'>
          {result.isLoading ? t('Loading') : t('Unavailable')}
        </Badge>
      )}
    </Table.Td>
  )
}
